/**
 * SituationTiles — "The situation" on a thread's brief (kanban 538d6a0d; Jess
 * picked layout D, "situation tiles", on the thread-brief layouts review).
 *
 * One tile per key fact Merlin filed: the topic, the number or state it leads
 * with, and a status word and colour for how that fact looks today. Clicking
 * a tile opens the shared centered modal (components/common/Modal, the same
 * pop-out shape as the rest of the workspace) with the full fact, what Merlin
 * checked for it, the identifiers it read for it (kanban 1af16b1c — a
 * signal ref quotes the signal), the steps of the cause chain that rest on
 * it. What to do about it stays in Do next alone: the pop-out repeating it
 * was a third copy of the list. The tiles are built in merlinBrief.ts
 * (situationTiles).
 */
import { useState } from 'react';

import { Modal } from '../../components/common/Modal';
import { EvidenceRefs } from './EvidenceRefs';
import type { SituationTile, TileStatus } from './merlinBrief';
import styles from './SituationTiles.module.css';

const STATUS_WORD: Record<TileStatus, string> = {
  problem: 'Problem',
  watch: 'Watch',
  ok: 'OK',
  none: '',
};

const STEP_STATE_WORD: Record<SituationTile['steps'][number]['state'], string> = {
  past: '',
  now: 'now',
  next: 'not yet',
};

export function SituationTiles({
  tiles,
  signals = [],
}: {
  tiles: SituationTile[];
  /** The thread's loaded signals, so a tile's signal refs quote them. */
  signals?: ReadonlyArray<{ id: string; observation: string }>;
}) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const current = tiles.find((t) => t.key === openKey) ?? null;
  const close = () => setOpenKey(null);

  return (
    <div className={styles.wrap} aria-label="The situation">
      <div className={styles.head}>
        <span className={styles.label}>The situation</span>
        {/* One hint for the grid (Scott, 2026-10-06): each tile used to
            repeat "See the fact and its source" as well. */}
        <span className={styles.hint}>Click a tile for the full fact and where Merlin checked it</span>
      </div>
      <ul className={styles.grid}>
        {tiles.map((t) => (
          <li key={t.key}>
            <button
              type="button"
              className={styles.tile}
              data-status={t.status}
              aria-pressed={t.key === openKey}
              onClick={() => setOpenKey(t.key === openKey ? null : t.key)}
              // The tile's words are a customer, a topic and a headline; the
              // recorder step is the family, keyed by the tile (kanban 529c0851).
              data-action="qcp_situation_tile_open"
              data-target-tile={t.key}
            >
              <span className={styles.tileTop}>
                {t.label && <span className={styles.tileLabel}>{t.label}</span>}
                {STATUS_WORD[t.status] && (
                  <span className={styles.status} data-status={t.status}>
                    {STATUS_WORD[t.status]}
                  </span>
                )}
              </span>
              <span className={styles.headline}>{t.headline}</span>
            </button>
          </li>
        ))}
      </ul>
      {current && (
        <Modal isOpen onClose={close} title={current.label || 'Fact'} size="md">
          <div
            className={styles.detail}
            data-testid="situation-drawer"
            data-status={current.status}
            aria-label={`${current.label || 'Fact'}: ${current.headline}`}
          >
            <div className={styles.detailTop}>
              <strong className={styles.detailHeadline}>{current.headline}</strong>
              {STATUS_WORD[current.status] && (
                <span className={styles.status} data-status={current.status}>
                  {STATUS_WORD[current.status]}
                </span>
              )}
            </div>
            {current.fact !== current.headline && <p className={styles.fact}>{current.fact}</p>}
            {current.basis && (
              <div className={styles.block}>
                <span className={styles.blockLabel}>Merlin checked</span>
                <p>{current.basis}</p>
              </div>
            )}
            {current.evidence.length > 0 && (
              <div className={styles.block}>
                <EvidenceRefs refs={current.evidence} signals={signals} />
              </div>
            )}
            {current.steps.length > 0 && (
              <div className={styles.block}>
                <span className={styles.blockLabel}>How it got here</span>
                <ul className={styles.steps}>
                  {current.steps.map((s) => (
                    <li key={s.key} data-state={s.state}>
                      {s.when && <span className={styles.when}>{s.when}</span>}
                      <span>{s.title}</span>
                      {STEP_STATE_WORD[s.state] && (
                        <span className={styles.stateWord}>{STEP_STATE_WORD[s.state]}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

export default SituationTiles;
